// Phase 9.b — /disclosures route. Consolidates fee schedule pointer,
// adviser/CRD#/ADV link, custody language, and risk disclosures into one
// place. Backend data lives at GET /api/v1/disclosures/ (DisclosuresView).
import { useEffect, useState } from 'react';
import { PageHeader, KickerRow } from '../../components/shell';
import { api } from '../../lib/api';

interface Disclosures {
  adviser: {
    legal_name: string;
    crd_number: string | null;
    adv_url: string;
    contact_email: string;
    jurisdiction: string;
    charges_performance_fee: boolean;
  };
  custody: {
    venues: { name: string; role: string }[];
    language: string;
  };
  risk_disclosures: string[];
  fee_schedule_note: string;
}

export default function Disclosures() {
  const [data, setData] = useState<Disclosures | null | 'error'>(null);
  useEffect(() => {
    api<Disclosures>('/disclosures/').then(setData).catch(() => setData('error'));
  }, []);

  if (data === null) return <div className="view container"><p className="label-s">Loading…</p></div>;
  if (data === 'error') return <div className="view container"><p className="serif-body">Unable to load disclosures.</p></div>;

  return (
    <div className="view container">
      <PageHeader
        kicker="DISCLOSURES"
        title={<><em>Required</em> disclosures.</>}
        meta={<span className="label-s">UPDATED ON CHANGES TO REGULATORY STATUS</span>}
      />

      <section style={{ marginTop: 40 }}>
        <KickerRow kicker="ADVISER" />
        <table className="tbl" style={{ marginTop: 12 }}>
          <tbody>
            <tr><td className="label-s">LEGAL NAME</td><td>{data.adviser.legal_name}</td></tr>
            <tr><td className="label-s">JURISDICTION</td><td>{data.adviser.jurisdiction}</td></tr>
            <tr><td className="label-s">CRD NUMBER</td><td>{data.adviser.crd_number || <em style={{ color: 'var(--ink-3)' }}>Registration pending</em>}</td></tr>
            {/* LC-REV9-001 B1: only link an SEC record once a CRD exists —
                an unconditional adviserinfo link implies a registration
                Rev9 does not hold. */}
            <tr><td className="label-s">FORM ADV</td><td>{data.adviser.crd_number
              ? <a href={data.adviser.adv_url} target="_blank" rel="noreferrer" style={{ color: 'var(--ink)' }}>SEC adviserinfo →</a>
              : <em style={{ color: 'var(--ink-3)' }}>—</em>}</td></tr>
            <tr><td className="label-s">CONTACT</td><td><a href={`mailto:${data.adviser.contact_email}`} style={{ color: 'var(--ink)' }}>{data.adviser.contact_email}</a></td></tr>
            <tr><td className="label-s">PERFORMANCE FEES</td><td>{data.adviser.charges_performance_fee ? 'Charged where contractually agreed; see fee schedule on your monthly statement.' : 'No performance or incentive fees apply.'}</td></tr>
          </tbody>
        </table>
      </section>

      <section style={{ marginTop: 40 }}>
        <KickerRow kicker="CUSTODY" />
        <p className="serif-body" style={{ marginTop: 12 }}>{data.custody.language}</p>
        <table className="tbl" style={{ marginTop: 12 }}>
          <thead><tr><th>VENUE</th><th>ROLE</th></tr></thead>
          <tbody>
            {data.custody.venues.map((v, i) => (
              <tr key={i}><td>{v.name}</td><td className="serif-body">{v.role}</td></tr>
            ))}
          </tbody>
        </table>
      </section>

      <section style={{ marginTop: 40 }}>
        <KickerRow kicker="RISK DISCLOSURES" />
        <ul style={{ marginTop: 12, paddingLeft: 20 }}>
          {data.risk_disclosures.map((d, i) => (
            <li key={i} className="serif-body" style={{ marginBottom: 8 }}>{d}</li>
          ))}
        </ul>
      </section>

      <section style={{ marginTop: 40 }}>
        <KickerRow kicker="FEE SCHEDULE" />
        <p className="serif-body" style={{ marginTop: 12 }}>{data.fee_schedule_note}</p>
      </section>
    </div>
  );
}
